import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface WeatherData {
  temp: number;
  condition: 'clear' | 'clouds' | 'rain' | 'snow';
  description: string;
}

const WeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Call our backend proxy to fetch weather data (keeps API key secure)
        const API_BASE_URL =
          import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

        const response = await axios.get(`${API_BASE_URL}/weather`);

        const weatherMain = response.data.weather[0].main.toLowerCase();
        let condition: WeatherData['condition'] = 'clear';

        if (weatherMain.includes('cloud')) {
          condition = 'clouds';
        } else if (
          weatherMain.includes('rain') ||
          weatherMain.includes('drizzle') ||
          weatherMain.includes('thunderstorm')
        ) {
          condition = 'rain';
        } else if (weatherMain.includes('snow')) {
          condition = 'snow';
        }

        setWeather({
          temp: Math.round(response.data.main.temp),
          condition,
          description: response.data.weather[0].description,
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching weather:', err);
        setError(true);
        setLoading(false);
      }
    };

    fetchWeather();
    // Refresh weather every 30 minutes
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const renderWeatherIcon = () => {
    switch (weather?.condition) {
      case 'clear':
        return <SunIcon />;
      case 'clouds':
        return <CloudIcon />;
      case 'rain':
        return <RainIcon />;
      case 'snow':
        return <SnowIcon />;
      default:
        return <SunIcon />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gradient-to-br from-primary-400 to-primary-500 rounded-lg shadow-md">
        <div className="animate-pulse w-12 h-12 bg-primary-300 rounded-full"></div>
        <div className="animate-pulse h-4 w-20 bg-primary-300 rounded-sm"></div>
      </div>
    );
  }

  if (error) {
    // Show a simple fallback widget instead of hiding completely
    return (
      <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-primary-400 to-primary-500 rounded-lg shadow-lg">
        <div className="w-12 h-12 flex items-center justify-center">
          <SunIcon />
        </div>
        <div className="text-white">
          <div className="text-xs font-medium opacity-90">Destin, FL</div>
          <div className="text-lg font-bold leading-tight">Sunny ☀️</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-gradient-to-br from-primary-400 to-primary-500 rounded-lg shadow-lg hover:shadow-xl transition-shadow">
      <div className="w-12 h-12 flex items-center justify-center">
        {renderWeatherIcon()}
      </div>
      <div className="text-white">
        <div className="text-xs font-medium opacity-90">Destin, FL</div>
        <div className="text-2xl font-bold leading-tight">
          {weather?.temp}°F
        </div>
      </div>
    </div>
  );
};

// Animated Sun Icon
const SunIcon: React.FC = () => (
  <svg
    className="w-full h-full animate-spin-slow text-yellow-300"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="5" className="drop-shadow-lg" />
    <g className="animate-pulse">
      <line
        x1="12"
        y1="1"
        x2="12"
        y2="3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="12"
        y1="21"
        x2="12"
        y2="23"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="4.22"
        y1="4.22"
        x2="5.64"
        y2="5.64"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="18.36"
        y1="18.36"
        x2="19.78"
        y2="19.78"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="1"
        y1="12"
        x2="3"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="21"
        y1="12"
        x2="23"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="4.22"
        y1="19.78"
        x2="5.64"
        y2="18.36"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <line
        x1="18.36"
        y1="5.64"
        x2="19.78"
        y2="4.22"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

// Animated Cloud Icon
const CloudIcon: React.FC = () => (
  <svg
    className="w-full h-full text-white animate-float"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M6.5 19C4.57 19 3 17.43 3 15.5C3 13.58 4.46 12.03 6.36 12C6.76 9.65 8.81 8 11.28 8C13.76 8 15.8 9.66 16.2 12C18.09 12.09 19.63 13.68 19.63 15.63C19.63 17.61 18.04 19.2 16.06 19.2L6.5 19Z" />
  </svg>
);

// Animated Rain Icon
const RainIcon: React.FC = () => (
  <svg
    className="w-full h-full text-white"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.5 16C4.57 16 3 14.43 3 12.5C3 10.58 4.46 9.03 6.36 9C6.76 6.65 8.81 5 11.28 5C13.76 5 15.8 6.66 16.2 9C18.09 9.09 19.63 10.68 19.63 12.63C19.63 14.61 18.04 16.2 16.06 16.2L6.5 16Z"
      fill="currentColor"
    />
    <line
      x1="7"
      y1="18"
      x2="7"
      y2="21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="animate-rain-drop"
    />
    <line
      x1="11"
      y1="17"
      x2="11"
      y2="20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="animate-rain-drop-delayed"
    />
    <line
      x1="15"
      y1="18"
      x2="15"
      y2="21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="animate-rain-drop"
    />
  </svg>
);

// Animated Snow Icon
const SnowIcon: React.FC = () => (
  <svg
    className="w-full h-full text-white"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.5 16C4.57 16 3 14.43 3 12.5C3 10.58 4.46 9.03 6.36 9C6.76 6.65 8.81 5 11.28 5C13.76 5 15.8 6.66 16.2 9C18.09 9.09 19.63 10.68 19.63 12.63C19.63 14.61 18.04 16.2 16.06 16.2L6.5 16Z"
      fill="currentColor"
    />
    <g className="animate-snow-fall">
      <circle cx="7" cy="19" r="1" fill="currentColor" />
      <circle cx="11" cy="18" r="1" fill="currentColor" />
      <circle cx="15" cy="19" r="1" fill="currentColor" />
      <circle cx="9" cy="21" r="1" fill="currentColor" />
      <circle cx="13" cy="21" r="1" fill="currentColor" />
    </g>
  </svg>
);

export default WeatherWidget;
